import {
  mapCheckJobStatusDto,
  mapCheckReportDto,
  type CheckJobResponseDto,
  type CheckReportDto,
} from '@/types/check.mappers';
import type { CheckBatchStatus, CheckReport, CheckTarget } from '@/types/check';
import { ApiHttpError } from '@/types';

import type { CheckIdempotentOpts, CheckService, CheckStartTarget } from '../checkService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`CheckService` —— 真實實作(R213 起 `services/index.ts` 注入這支)。
 * -----------------------------------------------------------------------------
 * 對帳來源 = `creative-test-v2/docs/88-asset-check-api-reference.md`(逐條 §);BE
 * `app/asset_check/` P1 全數 ship + dev 已部署(docs/87 開頭,tag `dev-update-v13-4`)。
 *
 * ⚠️ **兩件雲端設定尚未人工執行**(docs/87;CI 沒有這兩個 job),行為差異不是串錯:
 *   - `firestore:rules` 未部署 → job doc onSnapshot `permission-denied`(REST 快照仍可用)
 *   - `firestore:indexes` 未部署 → **不帶 `report_id` 的「查最新報告」會噴缺索引錯誤**
 *     → 所以 `getReport` 拿得到 `report_id` 就一定要帶(docs/87 §9)
 *
 * 語意對齊 fixture 實作(兩實作必須逐條相同):
 *   - `getReport` 404 → **null**(H 節:404 = 未檢查,不是錯誤)
 *   - `getBatch` 404 → null(job 不存在 → store 清 stale)
 *   - `cancelBatch` 冪等(BE 保證;FE 對 404 靜默 — 已結束的 job 取消視為成功)
 *   - `subscribeBatch` **回 no-op**:http 版的即時進度走 Firestore onSnapshot
 *     (`useCheckBatchListener` 以 `firestorePath` 訂閱;repo listener-only 拍板,
 *     不退 polling)。fixture 版才用 timer 推送 — 同一個 hook 兩條路都吃。
 *
 * transport:`/api/asset_check` 與 `/api/assets` 皆非 GROUP_HEADER_EXEMPT →
 * `httpRequestWithRefresh` 自動帶 `X-Current-Group` + cookie + CSRF(寫操作)。
 */

const PATH = {
  jobs: '/api/asset_check/jobs',
  job: (jobId: string) => `/api/asset_check/jobs/${encodeURIComponent(jobId)}`,
  jobCancel: (jobId: string) => `/api/asset_check/jobs/${encodeURIComponent(jobId)}/cancel`,
  // 檢查目標 = 素材庫 item 複合鍵(H 節首條:無全域 asset id → 兩段 path)
  report: (libId: string, itemId: string) =>
    `/api/asset_libraries/${encodeURIComponent(libId)}/items/${encodeURIComponent(itemId)}/check-report`,
  check: (libId: string, itemId: string) =>
    `/api/asset_libraries/${encodeURIComponent(libId)}/items/${encodeURIComponent(itemId)}/check`,
  copyApply: (libId: string, itemId: string) =>
    `/api/asset_libraries/${encodeURIComponent(libId)}/items/${encodeURIComponent(itemId)}/check-report/copy-apply`,
  groupItems: (libId: string, itemId: string) =>
    `/api/asset_libraries/${encodeURIComponent(libId)}/items/${encodeURIComponent(itemId)}/check/group-items`,
} as const;

/** 404 → null(未檢查 / job 不存在);其餘錯誤照拋給呼叫端顯示。 */
function isNotFound(err: unknown): boolean {
  return err instanceof ApiHttpError && err.status === 404;
}

/** target → wire body(`group_key` 多角度群組素材要帶,否則報告無法聚合回產品卡)。 */
function targetBody(t: CheckStartTarget): Record<string, string> {
  return {
    lib_id: t.libId,
    item_id: t.itemId,
    // ⚠️ **只有這兩個鍵**(docs/88 §1 request 表)。BE request model 是
    // `extra="forbid"` → 多送任何欄位(R211 曾夾帶 `group_key`)整批 422、一件都不跑。
    // `groupKey` / `kind` / `name` / `made` 都是 FE 顯示 hint,留在本機。
  };
}

export function createHttpCheckService(config: HttpClientConfig): CheckService {
  return {
    /**
     * `?report_id=` 選填(§4:不帶 = 取最新一份)。**indexes 未部署前一律帶** ——
     * 「查最新」要走 composite index,沒部署就是缺索引錯誤(非 404 → 不會被下面的
     * `isNotFound` 轉成「未檢查」,而是報告頁的錯誤態)。見 docs/87 §9。
     */
    async getReport(target: CheckTarget, reportId?: string): Promise<CheckReport | null> {
      const url =
        PATH.report(target.libId, target.itemId) +
        (reportId ? `?report_id=${encodeURIComponent(reportId)}` : '');
      try {
        const env = await httpRequestWithRefresh<CheckReportDto>(config, 'GET', url);
        return mapCheckReportDto(unwrapResult(env, 'check.getReport'));
      } catch (err) {
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    /**
     * `POST /asset_check/jobs` — **回應本身就是第一份快照**(§2.1 三條同形)→ 直接
     * map 成 `CheckBatchStatus` 回傳,呼叫端不必再打一趟 GET。
     *
     * `idempotencyKey`:docs/88 §1 明言不帶就**完全沒有重送保護**,double-click 會
     * 建兩個 job、扣兩份點(rate limit 是 per-instance 近似值,擋不住)。
     */
    async startBatch(
      targets: CheckStartTarget[],
      opts?: CheckIdempotentOpts,
    ): Promise<CheckBatchStatus> {
      const env = await httpRequestWithRefresh<CheckJobResponseDto>(config, 'POST', PATH.jobs, {
        targets: targets.map(targetBody),
        ...(opts?.idempotencyKey ? { idempotency_key: opts.idempotencyKey } : {}),
        // R424.1 — 報告語系有值才上 wire(舊 BE `extra="forbid"` → 422)。
        ...(opts?.locale ? { locale: opts.locale } : {}),
      });
      const res = mapCheckJobStatusDto(unwrapResult(env, 'check.startBatch'));
      // developer-only(不進 DOM)→ 英文,避免 hai/no-hardcoded-cjk
      if (!res) throw new Error('check.startBatch: response missing job.job_id');
      return res;
    },

    /**
     * `GET /asset_check/jobs?limit=` — 進行中批次清單(§1.5;冷啟還原,不必先知道
     * job_id)。回應 `result.jobs[]` 的**每一筆都是 §2.1 的完整 job 形狀**
     * (`{job, items, firestore_paths, stages}`,BE `_job_response()` 同一支)→
     * 直接餵既有 mapper;認不出 `job.job_id` 的那筆丟掉,不讓半殘快照進 store。
     */
    async listActiveBatches(limit = 5): Promise<CheckBatchStatus[]> {
      // BE `Query(ge=1, le=20)`:超界會是 422,夾在這裡比讓呼叫端記規則可靠
      const n = Math.min(20, Math.max(1, Math.trunc(limit) || 1));
      const env = await httpRequestWithRefresh<{ jobs?: CheckJobResponseDto[] | null }>(
        config,
        'GET',
        `${PATH.jobs}?limit=${n}`,
      );
      const result = unwrapResult(env, 'check.listActiveBatches');
      return (result.jobs ?? [])
        .map((j) => mapCheckJobStatusDto(j))
        .filter((s): s is CheckBatchStatus => s !== null);
    },

    async getBatch(batchId: string): Promise<CheckBatchStatus | null> {
      try {
        const env = await httpRequestWithRefresh<CheckJobResponseDto>(
          config,
          'GET',
          PATH.job(batchId),
        );
        return mapCheckJobStatusDto(unwrapResult(env, 'check.getBatch'));
      } catch (err) {
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    /** cancel 回應 = 最新快照 + `cancelled` 計數(§3 明示不必再 GET)。 */
    async cancelBatch(batchId: string): Promise<CheckBatchStatus | null> {
      try {
        const env = await httpRequestWithRefresh<CheckJobResponseDto>(
          config,
          'POST',
          PATH.jobCancel(batchId),
        );
        return mapCheckJobStatusDto(unwrapResult(env, 'check.cancelBatch'));
      } catch (err) {
        // 冪等:job 已結束 / 不存在 → 視為取消成功(BE 端也是冪等,這是雙保險)
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    subscribeBatch(): () => void {
      // http 版不做 imperative 訂閱 — 即時進度走 Firestore onSnapshot
      // (`useCheckBatchListener` 讀 `batch.firestorePath` 交給 useFirestoreSubscription)。
      return () => {};
    },

    /**
     * 單素材重檢(獨立端點,**再扣 1 點**;§5)。回應 = 1-item job 快照,同 startBatch。
     * body 整個選填,但 `idempotency_key` 建議帶(double-click 會扣兩次)。
     */
    async checkSingle(
      target: CheckStartTarget,
      opts?: CheckIdempotentOpts,
    ): Promise<CheckBatchStatus | null> {
      const env = await httpRequestWithRefresh<CheckJobResponseDto>(
        config,
        'POST',
        PATH.check(target.libId, target.itemId),
        {
          ...(opts?.idempotencyKey ? { idempotency_key: opts.idempotencyKey } : {}),
          // R424.1 — 報告語系有值才上 wire(舊 BE `extra="forbid"` → 422)。
          ...(opts?.locale ? { locale: opts.locale } : {}),
        },
      );
      return mapCheckJobStatusDto(unwrapResult(env, 'check.checkSingle'));
    },

    /**
     * 校對稿套用/復原 —— body 是**整批 map**:`{applied: {"<field_k>:<rowIdx>": 文字|null}}`
     * (§6;null = 復原)。R211 預埋送單鍵扁平 `{row_key, text}` → 必填欄缺、100% 422。
     *
     * 回應 = **更新後的完整報告** → 直接回 domain 報告,呼叫端拿它刷新
     * `appliedTexts` / `groupStale`,不必再 GET 一次(也就不會有兩者不同步的窗)。
     * 「全部套用/全部復原」因此收成**一發**請求(原本 N 發,還會撞 60/min)。
     */
    async applyCopy(
      target: CheckTarget,
      applied: Record<string, string | null>,
      reportId?: string,
    ): Promise<CheckReport | null> {
      const env = await httpRequestWithRefresh<CheckReportDto>(
        config,
        'POST',
        PATH.copyApply(target.libId, target.itemId),
        { applied, ...(reportId ? { report_id: reportId } : {}) },
      );
      return mapCheckReportDto(unwrapResult(env, 'check.applyCopy'));
    },

    /**
     * 整組判定重跑(**扣 1 點**;前置 `groupStale=true`,否則 409
     * `CHECK_GROUP_ITEMS_NOT_STALE` — 跑完旗標即清,按第二次必 409)。
     * 回應本身就是新報告(§7 明示不必再 GET)。
     */
    async recheckGroupItems(target: CheckTarget, reportId?: string): Promise<CheckReport | null> {
      const env = await httpRequestWithRefresh<CheckReportDto>(
        config,
        'POST',
        PATH.groupItems(target.libId, target.itemId),
        reportId ? { report_id: reportId } : {},
      );
      return mapCheckReportDto(unwrapResult(env, 'check.recheckGroupItems'));
    },
  };
}
